import { PropsWithChildren, FC } from 'react'
import { Alert, Drawer, Skeleton, Space } from 'antd'
import _ from 'lodash'

import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import {
    ExceptionFailuresFilterInput,
    useExceptionFailuresQuery,
} from '~/gql/graphql'
import { MFG_PARTNER_PATH } from '~/redux-modules/preferences'

import ExceptionsDrawerTitle from './DrawerTitle'
import { ERROR_MESSAGE, FETCH_FAILURE_RECORDS_LIMIT } from './constants'
import ExceptionsDrawerBody from './DrawerBody'
import {
    ExceptionFailure,
    ExceptionFailuresDrawerTitleType,
    RefetchExceptionsVariablesType,
} from './types'
import styles from './styles.css'

const WIDTH = '787px'

type ExceptionFailuresDrawerPropsType = {
    onClose: () => void
    drawerData: ExceptionFailuresDrawerTitleType
}

export const PLACEMENT = 'exception-failures-drawer'

export const ExceptionFailuresSideDrawer: FC<
    PropsWithChildren<ExceptionFailuresDrawerPropsType>
> = props => {
    const { onClose, drawerData } = props
    const filters = {} as ExceptionFailuresFilterInput

    if (drawerData.jobRequestId) {
        filters.jobRequestId = drawerData.jobRequestId
    } else {
        filters.jobId = drawerData.jobData.id
    }
    const {
        data: exceptionFailuresData,
        loading,
        error,
    } = useExceptionFailuresQuery({
        variables: {
            filters,
            limit: FETCH_FAILURE_RECORDS_LIMIT,
        },
    })

    const allExceptionFailures = exceptionFailuresData?.exceptionFailures.items

    const preScheduleExceptions = drawerData.showJobExceptions
        ? allExceptionFailures
              ?.filter(exception => exception.jobRequestId === null)
              .map(exception => ({ ...exception }))
        : []

    const postScheduleGroupedExceptions = _.groupBy(
        allExceptionFailures?.filter(
            exceptionFailure => exceptionFailure.jobRequestId !== null,
        ),
        'jobRequestId',
    )
    const postScheduleExceptionsForRendering: ExceptionFailure[][] = []

    if (drawerData.showJobRequestExceptions) {
        _.forEach(postScheduleGroupedExceptions, value => {
            const exceptionFailures = value.map(exceptionFailure => ({
                ...exceptionFailure,
            }))

            postScheduleExceptionsForRendering.push(exceptionFailures)
        })
    }

    return (
        <Drawer
            closable={false}
            maskClosable={true}
            className={styles.exceptionDrawerBody}
            onClose={event => {
                event.stopPropagation()
                onClose()
            }}
            width={WIDTH}
            title={
                <div onClick={event => event.stopPropagation()}>
                    <ExceptionsDrawerTitle
                        jobData={drawerData.jobData}
                        refetchExceptionsVariables={
                            {
                                filters,
                                limit: FETCH_FAILURE_RECORDS_LIMIT,
                            } as RefetchExceptionsVariablesType
                        }
                    />
                </div>
            }
            visible
        >
            <Box onClick={event => event.stopPropagation()}>
                {error ? (
                    <Alert
                        style={{ width: '100%' }}
                        message={ERROR_MESSAGE}
                        type="error"
                        showIcon
                    />
                ) : (
                    <Skeleton loading={loading} active>
                        <div className="exceptionsFailures">
                            {preScheduleExceptions &&
                                preScheduleExceptions.length > 0 && (
                                    <ExceptionsDrawerBody
                                        header="Pre scheduling exceptions"
                                        exceptionFailuresData={
                                            preScheduleExceptions
                                        }
                                    />
                                )}
                            {postScheduleExceptionsForRendering.map(
                                (items, key) => (
                                    <ExceptionsDrawerBody
                                        key={key}
                                        header={
                                            items[0]
                                                .manufacturingPartnerName ? (
                                                <Space>
                                                    <Text>
                                                        {items[0].jobRequestId}
                                                    </Text>
                                                    <Link
                                                        name="view-mp-details"
                                                        placement={PLACEMENT}
                                                        data-test-target="view-mp-details"
                                                        href={`${MFG_PARTNER_PATH}/${items[0].manufacturingPartnerId}/jobs`}
                                                        target="_blank"
                                                    >
                                                        <Text>
                                                            {
                                                                items[0]
                                                                    .manufacturingPartnerName
                                                            }
                                                        </Text>
                                                    </Link>
                                                </Space>
                                            ) : (
                                                `${items[0].jobRequestId}`
                                            )
                                        }
                                        exceptionFailuresData={items}
                                    />
                                ),
                            )}
                        </div>
                    </Skeleton>
                )}
            </Box>
        </Drawer>
    )
}
